<template>

  <div class="vflex">

    <MessageList
      class="vflex-row-1"
      :messages="messages"
      :loading="loading"
      :loading-more="loadingMore"
      :has-more="hasMore"
      @load-more="loadMore"
      @seen="$emit('seen')" />

    <div
      v-if="error"
      class="alert alert-warning mb-0 rounded-0 small">
      {{ error }}
    </div>

    <MessageComposer
      :channels="availableChannels"
      :sending="sending"
      :session-open="sessionOpen"
      :is-agent-enabled="isAgentEnabled"
      :saving-agent="savingAgent"
      @channel="connectionId = $event"
      @take-control="$emit('take-control')"
      @send="send" />
  </div>

</template>

<script>
import MessageList from './MessageList'
import MessageComposer from '../composer/MessageComposer'

// only used to key the optimistic messages while they are going out
let sequence = 0

export default {
  name: 'MessageThread',
  components: {
    MessageList,
    MessageComposer
  },
  props: {
    conversation: {
      type: Object,
      default: null
    },
    channels: {
      type: Array,
      default: () => []
    },
    // the conversational agent is answering this conversation
    isAgentEnabled: {
      type: Boolean,
      default: false
    },
    // the take over is being persisted
    savingAgent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      loadingMore: false,
      // one send action can produce several messages: they go out at the same time
      sendingCount: 0,
      error: null,
      pending: [],
      // the outgoing channel picked in the composer
      connectionId: null
    }
  },
  computed: {
    conversationId () {
      return this.conversation && this.conversation._id
    },
    sending () {
      return this.sendingCount > 0
    },
    storeMessages () {
      // only expose the store messages when they belong to this conversation
      if (this.$store.getters.getMessagesConversationId !== this.conversationId) {
        return []
      }
      return this.$store.getters.getMessages
    },
    messages () {
      return this.storeMessages.concat(this.pending)
    },
    paginate () {
      return this.$store.getters.getMessagesPaginate || {}
    },
    hasMore () {
      return !!(this.paginate.page && this.paginate.pages && this.paginate.page < this.paginate.pages)
    },
    availableChannels () {
      return this.channels.filter((channel) => { return channel.status === 'active' })
    },
    selectedChannel () {
      return this.availableChannels.find((channel) => { return channel._id === this.connectionId }) || null
    },
    /*
    sessionOpen
    WhatsApp 24h service window: free text is only allowed within 24h of the
    contact's last inbound message on the channel it is being sent from. any
    other channel has no such window and is always open
    */
    sessionOpen () {
      const channel = this.selectedChannel

      if (!channel) {
        return false
      }

      if (channel.integration !== 'whatsapp') {
        return true
      }

      const inbound = this.storeMessages
        .slice()
        .reverse()
        .find((message) => {
          return message.direction === 'inbound' && message.connection_id === channel._id
        }) || null

      // the contact never answered on this channel: it has no window open
      if (!inbound) {
        return false
      }

      const elapsed = new Date() - new Date(inbound.sent_at || inbound.created_at).getTime()
      return elapsed >= 0 && elapsed < 24 * 60 * 60 * 1000
    }
  },
  watch: {
    conversationId () {
      this.pending = []
      this.loadMessages()
    }
  },
  created () {
    this.loadMessages()
  },
  methods: {
    /*
    loadMessages
    */
    loadMessages () {
      if (!this.conversationId) {
        return
      }
      this.loading = true
      this.error = null

      this.$store
      .dispatch('selectMessages', { conversationId: this.conversationId })
      .then(() => {
        // opening the thread is what makes the conversation read
        this.$emit('seen')
      })
      .catch(() => {
        this.error = this.$t('Could not load the conversation')
      })
      .finally(() => {
        this.loading = false
      })
    },
    /*
    loadMore
    loads older messages (history)
    */
    loadMore () {
      if (this.loadingMore || !this.hasMore) {
        return
      }
      this.loadingMore = true

      this.$store
      .dispatch('loadMoreMessages', this.paginate.page + 1)
      .catch(() => {})
      .finally(() => {
        this.loadingMore = false
      })
    },
    /*
    send
    optimistically shows the outbound message, then persists it
    */
    send (payload) {
      sequence = sequence + 1

      const tempId = 'pending-' + sequence
      const optimistic = {
        _id: tempId,
        direction: 'outbound',
        channel_type: 'whatsapp',
        type: payload.type,
        content: payload.content || null,
        // the media object itself, so the bubble can show it before the api answers
        attachment_media_ids: payload.media ? [payload.media] : [],
        sender_user_id: this.$store.getters.getMe ? this.$store.getters.getMe._id : null,
        delivery_status: 'sending',
        sent_at: new Date().toISOString(),
        _pending: true,
        _failed: false
      }
      this.pending.push(optimistic)
      this.sendingCount = this.sendingCount + 1
      this.error = null

      // body of POST /messages: { connection_id, type, content, attachment_media_ids }
      const data = {
        connection_id: payload.connection_id,
        type: payload.type,
        content: payload.content || null
      }

      if (payload.attachment_media_ids && payload.attachment_media_ids.length) {
        data.attachment_media_ids = payload.attachment_media_ids
      }

      this.$store
      .dispatch('sendMessage', { conversationId: this.conversationId, data: data })
      .then(() => {
        this.pending = this.pending.filter((item) => { return item._id !== tempId })
      })
      .catch((err) => {
        const item = this.pending.find((message) => { return message._id === tempId })
        if (item) {
          item._failed = true
          item.delivery_status = 'failed'
        }
        this.error = (err.response && err.response.data && err.response.data.message) || this.$t('Could not send the message')
      })
      .finally(() => {
        this.sendingCount = this.sendingCount - 1
      })
    }
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/Messaging/thread/MessageThread.vue